/**
 * Web Push subscription helper.
 * Handles VAPID public key fetch, converting base64url to Uint8Array,
 * subscribing via the browser's PushManager, and posting the subscription
 * to the backend so the server can send alerts to this device.
 */
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const currentPermission = () =>
  typeof Notification !== "undefined" ? Notification.permission : "denied";

export const subscribeToPush = async ({ role = "housekeeping", deviceLabel = "" } = {}) => {
  if (!pushSupported()) throw new Error("Push notifications are not supported on this device.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error(`Notification permission is ${perm}.`);

  const reg = await navigator.serviceWorker.ready;
  // Fetch the VAPID public key from the backend
  const keyResp = await api.get("/push/vapid-public-key");
  const key = keyResp.data?.public_key;
  if (!key) throw new Error("Server didn't return a VAPID public key.");

  // Some browsers keep an old sub — unsubscribe first to force refresh
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try { await existing.unsubscribe(); } catch (e) { /* ignore */ }
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    role,
    device_label: deviceLabel || navigator.userAgent.slice(0, 80),
  });
  return sub;
};

export const unsubscribeFromPush = async () => {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try { await api.post("/push/unsubscribe", { endpoint }); } catch (e) { /* ignore */ }
  return true;
};
